import { z } from 'zod';
import { useMemo } from 'react';
import { format, parse } from 'date-fns';
import { createProfileFormValidation } from '@lp/features/Profile/utils';
import { NameByValue, SelectItemType } from '@/types';
import { ScrubSize, LocationPreference, EmployeeComposed, EmployeeRole } from '@/api/apis/resources';
import { STATE_SCHEMA } from '@/utils/helpers';
import { ProfileFormType } from './types';
import { TMDXFile } from '@/api/apis/files';
import { CCResourceType } from '@/api/apis/resources/schedule';
import { OrganType } from '@/api/apis/case';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { DEFAULT_COUNTRY_ISO2 } from '@/constants/location.constants';

export const sizes: SelectItemType[] = [
  { value: 'XS', name: 'XS' },
  { value: 'S', name: 'S' },
  { value: 'M', name: 'M' },
  { value: 'L', name: 'L' },
  { value: 'XL', name: 'XL' },
  { value: 'XXL', name: 'XXL' },
];

export const getClinicalCoordinatorType = (type: CCResourceType | string): string => {
  switch (type) {
    case CCResourceType.TC1:
      return 'TCI';
    case CCResourceType.TC2:
      return 'TCII';
    case CCResourceType.TC3:
      return 'TCIII';
    default:
      return '';
  }
};

const getSpecificationsComputedData = (data: boolean, count: number) => {
  const daySpelling = count === 1 ? 'day' : 'days';

  return data ? `Yes (${count} ${daySpelling})` : 'No (0 days)';
};

export const getDefaultValues = (data: EmployeeComposed): ProfileFormType => {
  return {
    hireDate: data.hireDate ? format(parse(data.hireDate, 'yyyy-MM-dd', new Date()), 'MM / dd / yyyy') : '',
    weight: data.weight,
    scrubSizeTop: data.scrubSizeTop,
    scrubSizeBottom: data.scrubSizeBottom,
    tsaPrecheckNumber: data.tsaPrecheckNumber,
    phone: data.phone || '',
    locationPreference: data.locationPreference,
    profilePhoto: data.profilePhoto || '',
    addressId: data.homeLocation?.id || '',
    addressLine: data.homeLocation?.addressLine || '',
    city: data.homeLocation?.city || '',
    state: data.homeLocation?.state || null,
    postalCode: data.homeLocation?.postalCode || '',
    country: data.homeLocation?.country || DEFAULT_COUNTRY_ISO2,
    baseLocationId: data.baseLocation?.id || '',
    longitude: data.homeLocation?.longitude || 0,
    latitude: data.homeLocation?.latitude || 0,
    closestAirportForDeploymentId: data?.closestAirportForDeployment?.maoId.toString(),
    extraCallsComputed: getSpecificationsComputedData(data.canWorkOnExtraShifts, data.extraShiftsCount),
    canTravelComputed: getSpecificationsComputedData(data.canTravel, data.travelShiftsCount),
    clinicalCoordinatorType: (data.clinicalCoordinatorType as CCResourceType) || null,
    homeLocationTimezone: data?.homeLocation?.timezone || '',
    roles: data.roles,
    organType: data.organType || [],
  };
};

export const userSchema = (composedSubdivisionsList: Record<string, NameByValue[]>) =>
  z
    .object({
      hireDate: z.string({ error: 'Select hire date' }),
      profilePhoto: z.string().optional(),
      photoFile: z.custom<TMDXFile>().optional(),
      tsaPrecheckNumber: z.string().nullable(),
      weight: z.number().positive('Provide weight').nullable(),
      scrubSizeTop: z.enum(ScrubSize).nullable(),
      scrubSizeBottom: z.enum(ScrubSize).nullable(),
      phone: z.string(),
      baseLocationId: z.string().nullable(),
      closestAirportForDeploymentId: z.string().optional(),
      locationPreference: z.enum(LocationPreference),
      addressId: z.string().min(1, 'Provide address'),
      addressLine: z.string().min(1, 'Provide address'),
      city: z.string().min(1, 'Provide city'),
      state: STATE_SCHEMA,
      postalCode: z.string().min(1, 'Provide postal code'),
      country: z.string(),
      latitude: z.number().min(-90).max(90),
      longitude: z.number().min(-180).max(180),
      homeLocationTimezone: z.string(),
      roles: z.enum(EmployeeRole, { error: 'Invalid Role' }).array().nonempty('Provide role'),
      organType: z.array(z.enum(OrganType)),
      clinicalCoordinatorType: z.enum(CCResourceType).nullable(),
    })
    .superRefine(createProfileFormValidation(composedSubdivisionsList));

export const useUserSchema = () => {
  const { composedSubdivisionsList, isLoading } = useCountriesData();
  const schema = useMemo(() => userSchema(composedSubdivisionsList), [composedSubdivisionsList]);
  return { schema, isLoading };
};

export const LOCATION_PREFERENCES: SelectItemType[] = [
  { value: LocationPreference.Base, name: 'Base' },
  { value: LocationPreference.Home, name: 'Home' },
];
